/**
 * Build a compact profile summary for the Orella Agent context
 * Used by both HomeScreen and CaseHome to pass profile completeness + existing data
 * v132.6.0: Added contactList allergies, social/lifestyle data
 * v170.0.0: Added insurance, family history conditions, photo ID status
 */
import { formatMoneyCents } from './money'
import { hasCardRef } from './cardRef'
import { allLevelSpends } from './coverageLevels'
// TEST 1.03 — ONE way of saying it. This function already guarded its FAMILY half with
// Array.isArray and left the medical half on `|| []`, which substitutes for null and
// undefined ONLY: a truthy non-array walked straight into .map and white-screened the
// whole app from GlobalAgentProvider's useMemo. toConditionList IS that Array.isArray
// guard, in one place, for both halves — and it COERCES the legacy object shape back to
// the canonical list rather than answering [], because the bad value is persisted and an
// empty answer would silently drop conditions the patient was told had been imported.
import { toConditionList } from './profileConditions'
export function buildProfileSummary(profile) {
  if (!profile) return {}
  const a = profile.allergies || {}
  const m = profile.medications || {}
  const mh = profile.medicalHistory || {}
  const fh = profile.familyHistory || {}
  const sl = profile.socialLifestyle || {}
  const ins = profile.insurance || {}
  const id = profile.identification || {}

  const allergyNames = [...(a.medList || []), ...(a.foodList || []), ...(a.contactList || [])].map(x => x.name).filter(Boolean)
  const medNames = (m.list || []).map(x => `${x.name}${x.dosage ? ' ' + x.dosage : ''}`).filter(Boolean)
  const condNames = toConditionList(mh.conditions).map(x => x.name).filter(Boolean)

  // Family history — conditions with relative flags
  const familyConditions = []
  // ★ otherConditions is read too: the family-history editor keeps every name outside the
  //   FAMILY_CONDITIONS catalog there, and an imported condition is nearly always one of
  //   those. Reading only `conditions` dropped them without a word.
  ;[...toConditionList(fh.conditions), ...toConditionList(fh.otherConditions)].forEach(c => {
    const relatives = []
    if (c.mother) relatives.push('mother')
    if (c.father) relatives.push('father')
    if (c.sibling) relatives.push('sibling')
    if (c.grandparent) relatives.push('grandparent')
    // ★ A recorded relation this app does not model (an import may say "aunt") still names
    //   a real family condition. Surfacing it as written beats discarding it silently.
    if (relatives.length === 0 && c.relation) relatives.push(String(c.relation))
    if (relatives.length > 0 && c.name) {
      familyConditions.push(`${c.name} (${relatives.join(', ')})`)
    }
  })

  // Insurance summary
  const insuranceParts = []
  if (ins.selfPay) {
    insuranceParts.push('Self-pay')
  } else if (ins.coverageType) {
    insuranceParts.push(ins.coverageType.toUpperCase())
    if (ins.provider) insuranceParts.push(ins.provider)
    if (ins.ipa) insuranceParts.push(`IPA: ${ins.ipa}`)
  }

  // Social/lifestyle summary for agent context
  const lifestyleParts = []
  if (sl.smoking && sl.smoking !== 'never' && sl.smoking !== 'no') lifestyleParts.push(`smoking: ${sl.smoking}`)
  if (sl.alcohol && sl.alcohol !== 'never' && sl.alcohol !== 'no') lifestyleParts.push(`alcohol: ${sl.alcohol}`)
  if (sl.exercise) lifestyleParts.push(`exercise: ${sl.exercise}`)
  if (sl.occupation) lifestyleParts.push(`occupation: ${sl.occupation}`)

  // v227.13.0 — verified in-network benefits snapshot for the Orella AI agent, so it
  // can answer the patient's "where do I stand" / "what's my copay" questions from
  // real eligibility data rather than guessing.
  const elig = ins.last_eligibility || ins.lastEligibility || null
  let benefits = ''
  if (elig && (elig.resultClass === 'active' || (elig.status || '').toLowerCase() === 'active')) {
    const d = formatMoneyCents
    const bp = []
    if (elig.planIdentifiers?.planName) bp.push(elig.planIdentifiers.planName)
    // PROD-587: no network claim. elig.inNetwork is the FIRST 271 line's benefit-level flag, not a
    //   statement that the doctor is in network ("in-network active" said that). orella-api ignores
    //   this string since v243.286.0 (PROD-588) and states its own facts with their networks.
    // TEST 3.10: every figure names its coverage level (individual / family), and a total and
    // a remaining are always from the same level (utils/coverageLevels.js — the flat fields
    // carry no level and are not read).
    for (const [kind, word] of [['deductible', 'deductible'], ['outOfPocket', 'out-of-pocket']]) {
      for (const s of allLevelSpends(elig, kind)) {
        if (s.total != null && s.remaining != null) bp.push(`${s.level} ${word} ${d(s.total - s.remaining)} of ${d(s.total)} met (${d(s.remaining)} remaining)`)
        else if (s.remaining != null) bp.push(`${s.level} ${word} ${d(s.remaining)} remaining`)
      }
    }
    if (elig.copaySpecialistCents != null) bp.push(`specialist copay ${d(elig.copaySpecialistCents)}`)
    if (elig.copayPcpCents != null) bp.push(`office copay ${d(elig.copayPcpCents)}`)
    if (elig.coinsurancePercent != null) bp.push(`coinsurance ${elig.coinsurancePercent}%`)
    if (Array.isArray(elig.benefits)) {
      elig.benefits.forEach((b) => {
        const nm = (b.serviceType || '').toLowerCase()
        if ((nm.includes('emergency') || nm.includes('urgent') || nm.includes('telehealth')) && b.copayCents != null) {
          bp.push(`${(b.serviceType || '').toLowerCase()} copay ${b.copayCents === 0 ? 'none' : d(b.copayCents)}`)
        }
      })
    }
    if (elig.coverageDates?.planEnd) bp.push(`active through ${elig.coverageDates.planEnd}`)
    benefits = bp.join('; ')
  }

  return {
    allergiesComplete: a.completed || false,
    medicationsComplete: m.completed || false,
    medicalHistoryComplete: mh.completed || false,
    familyHistoryComplete: fh.completed || false,
    socialLifestyleComplete: sl.completed || false,
    insuranceComplete: ins.completed || ins.selfPay || false,
    hasPhotoId: hasCardRef(id.idFront),
    allergies: allergyNames.length > 0 ? allergyNames.join(', ') : '',
    medications: medNames.length > 0 ? medNames.join(', ') : '',
    conditions: condNames.length > 0 ? condNames.join(', ') : '',
    familyHistory: familyConditions.length > 0 ? familyConditions.join(', ') : '',
    insurance: insuranceParts.length > 0 ? insuranceParts.join(' — ') : '',
    benefits,
    lifestyle: lifestyleParts.length > 0 ? lifestyleParts.join('; ') : '',
  }
}
